// src/Components/Broadcast/components/FilterPanel.js
import React, { useState, useEffect } from 'react';
import { Filter, Crown, Users, CheckCircle, X, Gift, BookOpen, MinusCircle, Settings, Eye, EyeOff, ChevronDown, ChevronUp, History, Calendar, Mail, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://artdownload2-production.up.railway.app';

export const FilterPanel = ({
  targetFilter,
  handleFilterChange,
  stats,
  selectedUsers,
  filteredUsers,
  products,
  productFilter,
  setProductFilter,
  gifts: externalGifts,
  giftFilter,
  setGiftFilter,
  excludedUsers,
  excludedGroups,
  excludedProducts,
  combinationType,
  setCombinationType,
  excludeSentUsers,
  setExcludeSentUsers,
  addExcludedUser,
  removeExcludedUser,
  addExcludedGroup,
  removeExcludedGroup,
  addExcludedProduct,
  removeExcludedProduct,
  onApplyFilters,
  broadcastHistory = [],
  excludeByHistory = false,
  setExcludeByHistory,
  selectedHistoryItems = [],
  setSelectedHistoryItems,
  excludeSuccessUsers = false,
  setExcludeSuccessUsers,
  excludeFailedUsers = false,
  setExcludeFailedUsers,
  failedUsersOnly = false,
  setFailedUsersOnly,
  onResendToFailed
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [excludeUserId, setExcludeUserId] = useState('');
  const [showHiddenTrainings, setShowHiddenTrainings] = useState(false);
  const [availableGifts, setAvailableGifts] = useState([]);
  const [loadingGifts, setLoadingGifts] = useState(false);
  const [giftLoadError, setGiftLoadError] = useState(null);
  
  const [hiddenTrainings, setHiddenTrainings] = useState(() => {
    try {
      const saved = localStorage.getItem('hiddenTrainings');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const safeStats = stats || { total: 0, premium: 0, free: 0 };

  // Загрузка доступных подарков
  const loadGifts = async () => {
    try {
      setLoadingGifts(true);
      setGiftLoadError(null);
      
      console.log('🔄 Загрузка подарков с сервера...');
      
      const response = await axios.get(`${API_URL}/api/gift-broadcast/gifts`);
      
      console.log('📥 Ответ от сервера:', response.data);
      
      if (response.data.success) {
        const gifts = response.data.gifts || [];
        setAvailableGifts(gifts);
        console.log(`✅ Загружено ${gifts.length} подарков`);
        console.log('📋 Список подарков:', gifts.map(g => ({ code: g.code, title: g.title })));
      } else {
        console.error('❌ Ошибка загрузки подарков:', response.data.error);
        setGiftLoadError(response.data.error || 'Ошибка загрузки подарков');
      }
    } catch (error) {
      console.error('❌ Ошибка при загрузке подарков:', error);
      setGiftLoadError(error.message || 'Ошибка соединения');
    } finally {
      setLoadingGifts(false);
    }
  };

  // Загружаем подарки при открытии вкладки
  useEffect(() => {
    if (activeTab === 'gifts') {
      loadGifts();
    }
  }, [activeTab]);

  // Также загружаем при первом рендере, если вкладка gifts активна
  useEffect(() => {
    if (activeTab === 'gifts') {
      loadGifts();
    }
  }, []);

  const recipientTypes = [
    { id: 'all', label: 'Все пользователи', icon: '👥', count: safeStats.total, color: 'blue' },
    { id: 'premium', label: 'Premium пользователи', icon: '👑', count: safeStats.premium, color: 'yellow' },
    { id: 'free', label: 'Free пользователи', icon: '📱', count: safeStats.free, color: 'gray' },
    { id: 'selected', label: 'Выбранные вручную', icon: '✓', count: selectedUsers?.length || 0, color: 'green' }
  ];

  // Условия отбора
  const conditions = [
    { 
      id: 'none', 
      label: 'Не использовать дополнительные фильтры', 
      icon: '➖', 
      description: 'Отправлять сообщение без дополнительных условий' 
    },
    { 
      id: 'has_products', 
      label: 'Есть доступ к выбранным продуктам', 
      icon: '📚', 
      description: 'Пользователи, у которых есть доступ к отмеченным курсам или тренингам' 
    },
    { 
      id: 'no_products', 
      label: 'Нет доступа к выбранным продуктам', 
      icon: '🚫', 
      description: 'Пользователи, у которых нет доступа к отмеченным курсам или тренингам' 
    },
    { 
      id: 'no_products_any', 
      label: 'Не покупали НИ ОДНОГО продукта', 
      icon: '📦', 
      description: 'Пользователи, у которых нет ни одного курса или тренинга' 
    },
    { 
      id: 'has_gifts', 
      label: 'Получили выбранные подарки', 
      icon: '🎁', 
      description: 'Пользователи, которые активировали выбранные подарки' 
    },
    { 
      id: 'no_gifts', 
      label: 'Не получали выбранные подарки', 
      icon: '🚫', 
      description: 'Пользователи, которые не активировали выбранные подарки' 
    },
    { 
      id: 'no_gifts_any', 
      label: 'Не получали НИ ОДНОГО подарка', 
      icon: '📦', 
      description: 'Пользователи, у которых нет ни одного активированного подарка' 
    }
  ];

  // Сохраняем скрытые тренинги в localStorage
  useEffect(() => {
    localStorage.setItem('hiddenTrainings', JSON.stringify(hiddenTrainings));
  }, [hiddenTrainings]);

  const handleProductSelect = (type, id) => {
    setProductFilter(prev => {
      const newFilter = { ...prev };
      if (type === 'course') {
        newFilter.courses = prev.courses.includes(id)
          ? prev.courses.filter(c => c !== id)
          : [...prev.courses, id];
      } else {
        newFilter.trainings = prev.trainings.includes(id)
          ? prev.trainings.filter(t => t !== id)
          : [...prev.trainings, id];
      }
      return newFilter;
    });
  };

  const handleGiftSelect = (gift) => {
    setGiftFilter(prev => {
      const currentFilter = prev || [];
      // Проверяем по code или id
      if (currentFilter.some(g => g.code === gift.code || g.id === gift.id)) {
        return currentFilter.filter(g => g.code !== gift.code && g.id !== gift.id);
      } else {
        return [...currentFilter, gift];
      }
    });
  };

  const handleExcludeUser = () => {
    if (excludeUserId.trim()) {
      addExcludedUser(excludeUserId.trim());
      setExcludeUserId('');
    }
  };

  const toggleHideTraining = (trainingId) => {
    setHiddenTrainings(prev => {
      if (prev.includes(trainingId)) {
        return prev.filter(id => id !== trainingId);
      } else {
        return [...prev, trainingId];
      }
    });
  };

  const toggleHistoryItem = (itemId) => {
    if (setSelectedHistoryItems) {
      setSelectedHistoryItems(prev => {
        if (prev.includes(itemId)) {
          return prev.filter(id => id !== itemId);
        } else {
          return [...prev, itemId];
        }
      });
    }
  };

  const selectAllHistory = () => {
    if (setSelectedHistoryItems) {
      if (selectedHistoryItems.length === broadcastHistory.length) {
        setSelectedHistoryItems([]);
      } else {
        setSelectedHistoryItems(broadcastHistory.map(item => item.id));
      }
    }
  };

  const getHistoryStats = () => {
    const selectedItems = broadcastHistory.filter(item => selectedHistoryItems.includes(item.id));
    const successUsers = new Set();
    const failedUsers = new Set();
    
    selectedItems.forEach(item => {
      if (item.successUsers && Array.isArray(item.successUsers)) {
        item.successUsers.forEach(userId => {
          if (userId) successUsers.add(userId);
        });
      }
      
      if (item.failedUsers && Array.isArray(item.failedUsers)) {
        item.failedUsers.forEach(fail => {
          const userId = typeof fail === 'string' ? fail : fail?.id;
          if (userId) failedUsers.add(userId);
        });
      }
    });
    
    return {
      count: selectedItems.length,
      successUsers: successUsers.size,
      failedUsers: failedUsers.size,
      totalUnique: new Set([...successUsers, ...failedUsers]).size,
      successUsersList: Array.from(successUsers),
      failedUsersList: Array.from(failedUsers)
    };
  };

  const handleResendToFailedFromHistory = () => {
    if (onResendToFailed && selectedHistoryItems.length > 0) {
      const allFailedUsers = [];
      const selectedItems = broadcastHistory.filter(item => selectedHistoryItems.includes(item.id));
      
      selectedItems.forEach(item => {
        if (item.failedUsers && Array.isArray(item.failedUsers) && item.failedUsers.length > 0) {
          item.failedUsers.forEach(fail => {
            const userId = typeof fail === 'string' ? fail : fail?.id;
            if (userId && !allFailedUsers.includes(userId)) {
              allFailedUsers.push(userId);
            }
          });
        }
      });
      
      if (allFailedUsers.length > 0) {
        onResendToFailed(allFailedUsers);
      }
    }
  };

  const applyFilters = () => {
    if (onApplyFilters) onApplyFilters();
  };

  const selectedProductsCount = (productFilter?.courses?.length || 0) + (productFilter?.trainings?.length || 0);
  const selectedGiftsCount = giftFilter?.length || 0;

  const needsProducts = ['has_products', 'no_products'].includes(combinationType);
  const needsGifts = ['has_gifts', 'no_gifts'].includes(combinationType);

  const visibleTrainings = products?.trainings?.filter(training => !hiddenTrainings.includes(training.id)) || [];
  const hiddenTrainingsList = products?.trainings?.filter(training => hiddenTrainings.includes(training.id)) || [];

  const historyStats = getHistoryStats();

  const getTargetFilterLabel = (filter) => {
    const labels = {
      'all': 'Все',
      'premium': 'Premium',
      'free': 'Free',
      'selected': 'Выбранные'
    };
    return labels[filter] || filter;
  };

  const getCombinationLabel = (type) => {
    const labels = {
      'has_products': 'Есть продукты',
      'no_products': 'Нет продуктов',
      'no_products_any': 'Нет продуктов вообще',
      'has_gifts': 'Есть подарки',
      'no_gifts': 'Нет подарков',
      'no_gifts_any': 'Нет подарков вообще',
      'none': 'Без условий'
    };
    return labels[type] || type;
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-4 border-b border-gray-700 bg-gray-800/50">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-purple-400" />
          <h2 className="text-lg font-semibold text-white">Фильтры получателей</h2>
        </div>
        <p className="text-xs text-gray-500 mt-1">Выберите кому отправить сообщение</p>
      </div>

      <div className="flex border-b border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('basic')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'basic'
              ? 'bg-gray-700 text-white border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
          }`}
        >
          👥 Кому
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'products'
              ? 'bg-gray-700 text-white border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
          }`}
        >
          📚 Продукты {selectedProductsCount > 0 && `(${selectedProductsCount})`}
        </button>
        <button
          onClick={() => setActiveTab('gifts')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'gifts'
              ? 'bg-gray-700 text-white border-b-2 border-purple-500'
              : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
          }`}
        >
          🎁 Подарки {selectedGiftsCount > 0 && `(${selectedGiftsCount})`}
        </button>
        <button
          onClick={() => setActiveTab('exclude')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'exclude'
              ? 'bg-gray-700 text-white border-b-2 border-red-500'
              : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
          }`}
        >
          🚫 Исключить
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'history'
              ? 'bg-gray-700 text-white border-b-2 border-green-500'
              : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
          }`}
        >
          📜 История {selectedHistoryItems.length > 0 && `(${selectedHistoryItems.length})`}
        </button>
      </div>

      <div className="p-4">
        {activeTab === 'basic' && (
          <div className="space-y-2">
            {recipientTypes.map(type => (
              <button
                key={type.id}
                onClick={() => { handleFilterChange(type.id); setTimeout(applyFilters, 100); }}
                className={`w-full p-3 rounded-lg text-left transition-all ${
                  targetFilter === type.id
                    ? `bg-${type.color}-900/30 border border-${type.color}-700`
                    : 'bg-gray-700/30 border border-gray-700 hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{type.icon}</span>
                    <span className="text-white font-medium">{type.label}</span>
                  </div>
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    targetFilter === type.id
                      ? `bg-${type.color}-900/50 text-${type.color}-300`
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {type.count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-4">
            <div className="bg-gray-900/50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">🔧 Условие отбора:</p>
              <div className="space-y-2">
                {conditions.filter(c => !c.id.includes('gift')).map(cond => (
                  <label
                    key={cond.id}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      combinationType === cond.id
                        ? 'bg-blue-900/30 border border-blue-700'
                        : 'hover:bg-gray-800'
                    }`}
                    onClick={() => { setCombinationType(cond.id); setTimeout(applyFilters, 100); }}
                  >
                    <input
                      type="radio"
                      name="condition"
                      checked={combinationType === cond.id}
                      onChange={() => {}}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{cond.icon}</span>
                        <span className="text-sm font-medium text-white">{cond.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{cond.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {needsProducts && (
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-400">📦 Выберите продукты для фильтрации:</p>
                  {hiddenTrainingsList.length > 0 && (
                    <button
                      onClick={() => setShowHiddenTrainings(!showHiddenTrainings)}
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                      {showHiddenTrainings ? (
                        <>
                          <EyeOff className="w-3 h-3" />
                          Скрыть скрытые
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3" />
                          Показать скрытые ({hiddenTrainingsList.length})
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Курсы */}
                {products?.courses?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-300 mb-2">📘 Курсы:</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {products.courses.map(course => (
                        <label key={course.id} className="flex items-center gap-2 p-1 rounded hover:bg-gray-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={productFilter?.courses?.includes(course.id) || false}
                            onChange={() => { handleProductSelect('course', course.id); setTimeout(applyFilters, 100); }}
                            className="rounded bg-gray-700 border-gray-600"
                          />
                          <span className="text-sm text-gray-300">{course.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Тренинги - видимые */}
                {visibleTrainings.length > 0 && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-300">🎯 Тренинги:</p>
                      <button
                        onClick={() => {
                          const newHidden = [...hiddenTrainings, ...visibleTrainings.map(t => t.id)];
                          setHiddenTrainings(newHidden);
                        }}
                        className="text-xs text-gray-500 hover:text-gray-400"
                        title="Скрыть все тренинги"
                      >
                        Скрыть все
                      </button>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {visibleTrainings.map(training => (
                        <div key={training.id} className="flex items-center justify-between group p-1 rounded hover:bg-gray-800">
                          <label className="flex items-center gap-2 flex-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={productFilter?.trainings?.includes(training.id) || false}
                              onChange={() => { handleProductSelect('training', training.id); setTimeout(applyFilters, 100); }}
                              className="rounded bg-gray-700 border-gray-600"
                            />
                            <span className="text-sm text-gray-300">{training.title}</span>
                          </label>
                          <button
                            onClick={() => toggleHideTraining(training.id)}
                            className="text-gray-500 hover:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Скрыть тренинг"
                          >
                            <EyeOff className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Скрытые тренинги */}
                {showHiddenTrainings && hiddenTrainingsList.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-500">🔒 Скрытые тренинги:</p>
                      <button
                        onClick={() => setHiddenTrainings([])}
                        className="text-xs text-blue-500 hover:text-blue-400"
                      >
                        Показать все
                      </button>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto bg-gray-800/30 rounded-lg p-2">
                      {hiddenTrainingsList.map(training => (
                        <div key={training.id} className="flex items-center justify-between group p-1 rounded hover:bg-gray-700">
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="checkbox"
                              checked={productFilter?.trainings?.includes(training.id) || false}
                              onChange={() => { handleProductSelect('training', training.id); setTimeout(applyFilters, 100); }}
                              className="rounded bg-gray-700 border-gray-600"
                            />
                            <span className="text-sm text-gray-500 line-through">{training.title}</span>
                          </div>
                          <button
                            onClick={() => toggleHideTraining(training.id)}
                            className="text-gray-500 hover:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Показать тренинг"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ВКЛАДКА ПОДАРКИ */}
        {activeTab === 'gifts' && (
          <div className="space-y-4">
            <div className="bg-gray-900/50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">🔧 Условие отбора по подаркам:</p>
              <div className="space-y-2">
                {conditions.filter(c => c.id.includes('gift')).map(cond => (
                  <label
                    key={cond.id}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      combinationType === cond.id
                        ? 'bg-purple-900/30 border border-purple-700'
                        : 'hover:bg-gray-800'
                    }`}
                    onClick={() => { setCombinationType(cond.id); setTimeout(applyFilters, 100); }}
                  >
                    <input
                      type="radio"
                      name="condition_gift"
                      checked={combinationType === cond.id}
                      onChange={() => {}}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{cond.icon}</span>
                        <span className="text-sm font-medium text-white">{cond.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{cond.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {needsGifts && (
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-400">🎁 Выберите подарки для фильтрации:</p>
                  <button
                    onClick={() => loadGifts()}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    disabled={loadingGifts}
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingGifts ? 'animate-spin' : ''}`} />
                    Обновить
                  </button>
                </div>
                
                {loadingGifts ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                    <p className="text-xs text-gray-500 mt-2">Загрузка подарков...</p>
                  </div>
                ) : giftLoadError ? (
                  <div className="text-center py-4 text-red-400 text-sm">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                    Ошибка загрузки подарков
                    <p className="text-xs text-gray-500 mt-1">{giftLoadError}</p>
                    <button
                      onClick={() => loadGifts()}
                      className="mt-2 px-4 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                    >
                      Попробовать снова
                    </button>
                  </div>
                ) : availableGifts.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <Gift className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                    Нет доступных подарков
                    <p className="text-xs text-gray-600 mt-1">Создайте подарки через админ-панель бота (/admin)</p>
                    <button
                      onClick={() => loadGifts()}
                      className="mt-2 px-4 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                    >
                      Проверить снова
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableGifts.map(gift => {
                      const isSelected = giftFilter?.some(g => g.code === gift.code || g.id === gift.id);
                      return (
                        <label
                          key={gift.code || gift.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-purple-900/30 border border-purple-700'
                              : 'hover:bg-gray-800 border border-transparent'
                          }`}
                          onClick={() => { handleGiftSelect(gift); setTimeout(applyFilters, 100); }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="rounded bg-gray-700 border-gray-600"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">{gift.title || gift.itemName || 'Подарок'}</span>
                              <span className="text-xs px-2 py-0.5 bg-gray-700 rounded-full text-gray-400 font-mono">
                                {gift.code}
                              </span>
                              {gift.isActive === false && (
                                <span className="text-xs px-2 py-0.5 bg-red-900/30 text-red-400 rounded-full">
                                  Неактивен
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                              <span>📚 {gift.itemName || '—'}</span>
                              <span>⏱️ {gift.duration || 14} дн.</span>
                              <span>📊 {gift.usageCount || 0} использ.</span>
                            </div>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
                
                {availableGifts.length > 0 && !loadingGifts && !giftLoadError && (
                  <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
                    <span>💡 Выберите подарки для фильтрации</span>
                    <button
                      onClick={() => {
                        setGiftFilter(availableGifts);
                        setTimeout(applyFilters, 100);
                      }}
                      className="text-purple-400 hover:text-purple-300"
                    >
                      Выбрать все
                    </button>
                    <button
                      onClick={() => {
                        setGiftFilter([]);
                        setTimeout(applyFilters, 100);
                      }}
                      className="text-gray-400 hover:text-gray-300"
                    >
                      Очистить
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'exclude' && (
          <div className="space-y-4">
            <div className="bg-gray-900/50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">🚫 Исключить пользователей по ID:</p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={excludeUserId}
                  onChange={(e) => setExcludeUserId(e.target.value)}
                  placeholder="Введите ID пользователя"
                  className="flex-1 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => { handleExcludeUser(); setTimeout(applyFilters, 100); }}
                  className="px-4 py-2 bg-red-900/30 text-red-300 text-sm rounded-lg border border-red-700 hover:bg-red-800/30 transition-colors"
                >
                  Добавить
                </button>
              </div>
              {excludedUsers?.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  <p className="text-xs text-gray-500 mb-1">Исключенные пользователи:</p>
                  {excludedUsers.map(userId => (
                    <div key={userId} className="flex items-center justify-between bg-gray-800/50 p-2 rounded">
                      <span className="text-sm text-gray-400 font-mono">{userId}</span>
                      <button
                        onClick={() => { removeExcludedUser(userId); setTimeout(applyFilters, 100); }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gray-900/50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">👥 Исключить группы:</p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludedGroups?.includes('premium') || false}
                    onChange={() => {
                      if (excludedGroups?.includes('premium')) removeExcludedGroup('premium');
                      else addExcludedGroup('premium');
                      setTimeout(applyFilters, 100);
                    }}
                    className="rounded bg-gray-700 border-gray-600"
                  />
                  <span className="text-sm text-gray-300">👑 Premium пользователи</span>
                </label>
                <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludedGroups?.includes('free') || false}
                    onChange={() => {
                      if (excludedGroups?.includes('free')) removeExcludedGroup('free');
                      else addExcludedGroup('free');
                      setTimeout(applyFilters, 100);
                    }}
                    className="rounded bg-gray-700 border-gray-600"
                  />
                  <span className="text-sm text-gray-300">📱 Free пользователи</span>
                </label>
              </div>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">📚 Исключить пользователей, у которых есть доступ к продуктам:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {products?.courses?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Курсы:</p>
                    {products.courses.map(course => {
                      const productKey = `course_${course.id}`;
                      return (
                        <label key={course.id} className="flex items-center gap-2 p-1 rounded hover:bg-gray-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={excludedProducts?.includes(productKey) || false}
                            onChange={() => { 
                              if (excludedProducts?.includes(productKey)) removeExcludedProduct(productKey);
                              else addExcludedProduct(productKey);
                              setTimeout(applyFilters, 100);
                            }}
                            className="rounded bg-gray-700 border-gray-600"
                          />
                          <span className="text-sm text-gray-300">{course.title}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                
                {products?.trainings?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1 mt-2">Тренинги:</p>
                    {products.trainings.map(training => {
                      const productKey = `training_${training.id}`;
                      return (
                        <label key={training.id} className="flex items-center gap-2 p-1 rounded hover:bg-gray-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={excludedProducts?.includes(productKey) || false}
                            onChange={() => { 
                              if (excludedProducts?.includes(productKey)) removeExcludedProduct(productKey);
                              else addExcludedProduct(productKey);
                              setTimeout(applyFilters, 100);
                            }}
                            className="rounded bg-gray-700 border-gray-600"
                          />
                          <span className="text-sm text-gray-300">{training.title}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-3">
              <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeSentUsers}
                  onChange={(e) => { setExcludeSentUsers(e.target.checked); setTimeout(applyFilters, 100); }}
                  className="rounded bg-gray-700 border-gray-600"
                />
                <div>
                  <span className="text-sm text-gray-300">🔄 Исключить пользователей, которые уже получали рассылку</span>
                  <p className="text-xs text-gray-500 mt-1">Позволяет отправлять сообщения только новым пользователям</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-400">📜 Выберите рассылки для фильтрации:</p>
                {broadcastHistory.length > 0 && (
                  <button
                    onClick={selectAllHistory}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    {selectedHistoryItems.length === broadcastHistory.length ? 'Снять все' : 'Выбрать все'}
                  </button>
                )}
              </div>
              
              {broadcastHistory.length === 0 ? (
                <div className="text-center py-4">
                  <History className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">История рассылок пуста</p>
                  <p className="text-xs text-gray-600 mt-1">Отправьте сообщение, чтобы появилась история</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {broadcastHistory.map(item => (
                      <label
                        key={item.id}
                        className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedHistoryItems.includes(item.id)
                            ? 'bg-blue-900/30 border border-blue-700'
                            : 'hover:bg-gray-800'
                        }`}
                        onClick={() => toggleHistoryItem(item.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedHistoryItems.includes(item.id)}
                          onChange={() => {}}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3 h-3 text-gray-500" />
                              <span className="text-xs text-gray-400">{formatShortDate(item.date)}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <CheckCircle className="w-3 h-3 text-green-400" />
                              <span className="text-green-400">{item.successCount || 0}</span>
                              <XCircle className="w-3 h-3 text-red-400 ml-1" />
                              <span className="text-red-400">{item.failedCount || 0}</span>
                            </div>
                          </div>
                          <p className="text-sm text-white mt-1 truncate">{item.text || '(без текста)'}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <Users className="w-3 h-3" />
                            <span>{getTargetFilterLabel(item.targetFilter)}</span>
                            {item.combinationType && item.combinationType !== 'none' && (
                              <>
                                <span>•</span>
                                <span>{getCombinationLabel(item.combinationType)}</span>
                              </>
                            )}
                            {item.hasImage && (
                              <>
                                <span>•</span>
                                <span>📷 с фото</span>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {selectedHistoryItems.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-700 space-y-3">
                      <div className="bg-gray-800/50 rounded-lg p-3 space-y-3">
                        <p className="text-xs text-gray-400 mb-2">🎯 Фильтрация по выбранным рассылкам:</p>
                        
                        {historyStats.successUsers > 0 && (
                          <div className="text-xs text-green-400 bg-green-900/20 p-2 rounded">
                            ✅ Найдено успешно получивших: {historyStats.successUsers} пользователей
                          </div>
                        )}
                        
                        {historyStats.failedUsers > 0 && (
                          <div className="text-xs text-red-400 bg-red-900/20 p-2 rounded">
                            ❌ Найдено с ошибками: {historyStats.failedUsers} пользователей
                          </div>
                        )}
                        
                        <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-700/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={excludeSuccessUsers}
                            onChange={(e) => {
                              if (setExcludeSuccessUsers) setExcludeSuccessUsers(e.target.checked);
                              setTimeout(applyFilters, 100);
                            }}
                            className="rounded bg-gray-700 border-gray-600"
                            disabled={historyStats.successUsers === 0}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                              <span className="text-sm text-gray-300">
                                Исключить тех, кому письмо успешно отправлено
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-green-900/50 text-green-300 rounded-full">
                                {historyStats.successUsers} пользователей
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Из выбранных рассылок будут исключены пользователи, получившие письмо без ошибок
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-700/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={excludeFailedUsers}
                            onChange={(e) => {
                              if (setExcludeFailedUsers) setExcludeFailedUsers(e.target.checked);
                              setTimeout(applyFilters, 100);
                            }}
                            className="rounded bg-gray-700 border-gray-600"
                            disabled={historyStats.failedUsers === 0}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <XCircle className="w-3.5 h-3.5 text-red-400" />
                              <span className="text-sm text-gray-300">
                                Исключить тех, у кого письмо не отправилось из-за ошибки
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-red-900/50 text-red-300 rounded-full">
                                {historyStats.failedUsers} пользователей
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Из выбранных рассылок будут исключены пользователи, у которых произошла ошибка при отправке
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-700/50 cursor-pointer border-t border-gray-700 pt-3">
                          <input
                            type="checkbox"
                            checked={failedUsersOnly}
                            onChange={(e) => {
                              if (setFailedUsersOnly) setFailedUsersOnly(e.target.checked);
                              setTimeout(applyFilters, 100);
                            }}
                            className="rounded bg-gray-700 border-gray-600"
                            disabled={historyStats.failedUsers === 0}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <RefreshCw className="w-3.5 h-3.5 text-yellow-400" />
                              <span className="text-sm text-gray-300 font-medium">
                                🔄 Только получатели с ошибками (для повторной отправки)
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-yellow-900/50 text-yellow-300 rounded-full">
                                {historyStats.failedUsers} пользователей
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Включите этот режим, чтобы отправить письмо повторно только тем, у кого была ошибка
                            </p>
                          </div>
                        </label>
                      </div>

                      {historyStats.failedUsers > 0 && onResendToFailed && (
                        <button
                          onClick={() => {
                            handleResendToFailedFromHistory();
                            setTimeout(applyFilters, 100);
                          }}
                          className="w-full px-4 py-2 bg-yellow-600/20 text-yellow-400 rounded-lg border border-yellow-600/50 hover:bg-yellow-600/30 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Повторно отправить письмо {historyStats.failedUsers} получателям с ошибками
                        </button>
                      )}

                      <div className="bg-gray-900/50 rounded-lg p-2 text-xs text-gray-500">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        Всего уникальных получателей в выбранных рассылках: {historyStats.totalUnique}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-gray-700 bg-gray-800/50 space-y-3">
        <button
          onClick={applyFilters}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <Filter className="w-4 h-4" />
          Применить фильтры
        </button>

        <div className="bg-gray-900/50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-400">Получателей:</span>
            </div>
            <div className="text-2xl font-bold text-white">{filteredUsers?.length || 0}</div>
          </div>
          
          <div className="mt-2 text-xs text-gray-500">
            {selectedProductsCount > 0 && (
              <div>📚 Выбрано продуктов: {selectedProductsCount}</div>
            )}
            {selectedGiftsCount > 0 && (
              <div>🎁 Выбрано подарков: {selectedGiftsCount}</div>
            )}
            {excludedUsers?.length > 0 && (
              <div>🚫 Исключено пользователей: {excludedUsers.length}</div>
            )}
            {excludeSentUsers && (
              <div>🔄 Исключены уже получившие рассылку</div>
            )}
            {excludeSuccessUsers && selectedHistoryItems.length > 0 && (
              <div className="text-green-500">✅ Исключены успешно получившие: {historyStats.successUsers} пользователей</div>
            )}
            {excludeFailedUsers && selectedHistoryItems.length > 0 && (
              <div className="text-red-500">❌ Исключены с ошибками: {historyStats.failedUsers} пользователей</div>
            )}
            {failedUsersOnly && selectedHistoryItems.length > 0 && (
              <div className="text-yellow-500">🔄 Только получатели с ошибками: {historyStats.failedUsers} пользователей</div>
            )}
            {hiddenTrainings.length > 0 && (
              <div className="text-yellow-500">🔒 Скрыто тренингов: {hiddenTrainings.length}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};